'use client'

import { Box, alpha, type Theme } from '@mui/material'
import { type ReactElement, type ReactNode } from 'react'

type TagVariant = 'solid' | 'outlined' | 'text' | 'filled'
// `surface` is intended for `variant="filled"` only — it uses
// `palette.heroOverlay` tokens defined by the redesign theme and is meant
// to remain legible on top of hero photography.
type TagColor = 'default' | 'primary' | 'surface'
type TagSize = 'xs' | 'sm' | 'md'
type TagShape = 'rect' | 'pill'

// heroOverlay tokens live in apps/teach's redesign palette and aren't
// declared as Palette keys here — Tag reads them through a local cast and
// falls back gracefully for any theme that doesn't define them.
interface PaletteWithHeroOverlay {
  heroOverlay?: { chipBg: string; chipText: string }
}

export interface TagProps {
  label: ReactNode
  icon?: ReactElement
  variant?: TagVariant
  color?: TagColor
  size?: TagSize
  shape?: TagShape
  uppercase?: boolean
  // Render the label semibold (600); "bold" emphasis in the redesign sense.
  bold?: boolean
  // Selected/active treatment shared across the redesign chrome: primary
  // text and border on a faint primary wash. Overrides the variant colors.
  selected?: boolean
  // Container-query breakpoint (MUI '@<px>' shorthand) below which the label
  // hides, leaving the icon visible. Requires an ancestor with
  // `containerType: 'inline-size'`.
  hideLabelAt?: string
}

export function Tag({
  label,
  icon,
  variant = 'outlined',
  color = 'default',
  size = 'md',
  shape = 'rect',
  uppercase = false,
  bold = false,
  selected = false,
  hideLabelAt,
}: TagProps) {
  return (
    <Box
      component="span"
      sx={[
        {
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.5,
          whiteSpace: 'nowrap',
          lineHeight: 1.4,
          borderRadius: shape === 'pill' ? '999px' : '4px',
        },
        (theme) => sizeSx(size, theme),
        (theme) => variantSx(variant, color, theme),
        selected &&
          ((theme: Theme) => ({
            color: 'primary.main',
            borderColor: 'primary.main',
            backgroundColor: alpha(theme.palette.primary.main, 0.05),
          })),
        uppercase && { textTransform: 'uppercase', letterSpacing: '0.06em' },
        bold && { fontWeight: 600 },
      ]}
    >
      {icon}
      {hideLabelAt ? (
        <Box
          component="span"
          sx={{ display: { '@': 'none', [hideLabelAt]: 'inline' } }}
        >
          {label}
        </Box>
      ) : (
        label
      )}
    </Box>
  )
}

// Custom redesign typography variants (body3/label1) aren't on the shared
// TypographyVariants type — like heroOverlay below, read them off the theme
// through a local cast and fall back to the literal for any theme that doesn't
// define them. Tag is redesign-only today; this keeps the redesign theme the
// single source of truth for its sizes.
interface TypographyWithRedesignVariants {
  body3?: { fontSize?: string | number }
  label1?: { fontSize?: string | number }
}

// Literal fallbacks mirror the redesign theme's body3/label1 sizes: without
// them an undefined fontSize INHERITS (16px) under the legacy theme, where
// flag-gated surfaces (e.g. the assessment cards/modal) also render Tags.
const MICRO_FONT_SIZE = '0.6875rem'

function sizeSx(size: TagSize, theme: Theme) {
  const typography =
    theme.typography as unknown as TypographyWithRedesignVariants
  if (size === 'xs') {
    return {
      fontSize: typography.body3?.fontSize ?? MICRO_FONT_SIZE,
      px: 1,
      py: 0.25,
    }
  }
  if (size === 'sm') {
    return {
      fontSize: typography.label1?.fontSize ?? MICRO_FONT_SIZE,
      px: 1,
      py: 0.25,
    }
  }
  return {
    fontSize: theme.typography.body2.fontSize,
    px: 1,
    py: '2px',
  }
}

function variantSx(variant: TagVariant, color: TagColor, theme: Theme) {
  const isPrimary = color === 'primary'
  const isSurface = color === 'surface'
  switch (variant) {
    case 'solid':
      return {
        backgroundColor: isPrimary
          ? alpha(theme.palette.primary.main, 0.12)
          : 'action.hover',
        color: isPrimary ? 'primary.main' : 'text.secondary',
      }
    case 'outlined':
      return {
        backgroundColor: 'background.paper',
        color: isPrimary ? 'primary.main' : 'text.secondary',
        border: '1px solid',
        borderColor: isPrimary ? 'primary.main' : 'grey.300',
        fontWeight: 500,
      }
    case 'text':
      return {
        color: isPrimary ? 'primary.main' : 'text.secondary',
        backgroundColor: 'transparent',
      }
    case 'filled': {
      if (isSurface) {
        const overlay = (theme.palette as unknown as PaletteWithHeroOverlay)
          .heroOverlay
        return {
          backgroundColor: overlay?.chipBg ?? theme.palette.text.primary,
          color: overlay?.chipText ?? theme.palette.background.paper,
        }
      }
      return {
        backgroundColor: isPrimary ? 'primary.light' : 'text.secondary',
        color: isPrimary
          ? theme.palette.primary.contrastText
          : theme.palette.background.paper,
      }
    }
  }
}
